import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useQuery, useQueryClient} from 'react-query';
import {useSearchParams} from 'react-router-dom';
import {Avatar} from '../../../../../../../../ui/atoms/avatar';
import {HorizontalDots} from '../../../../../../../../ui/atoms/icons/horizontal-dots';
import {MagnifyingGlassIcon} from '../../../../../../../../ui/atoms/icons/magnifying-glass';
import {Tooltip} from '../../../../../../../../ui/molecules/tooltip';
import {
  BeamAppliedFilter,
  BeamAppliedFilters,
  BeamFilterOption,
  BeamFilterPopover,
  BeamFilterSection,
} from '../../../../../../../../ui/molecules/filter-popover';
import {ItemLoaderLine} from '../../../../../../../../ui/organisms/item-loader-line';
import {activateNotification} from '../../../../../../../../ui/molecules/notification/activate-notification';
import {UserAPIRequestImpl} from '../../../../../../../../api/user-api/user-api-request';
import {UserAPIController} from '../../../../../../../../api/user-api/user-api-controller';
import {BaseURL} from '../../../../../../../../configs/request';
import {authStore} from '../../../../../../../../store/stores/auth-store';
import {useApiCalls} from '@hooks/init-calls';
import {useStoreContext} from '../../../../../../../../store/store-context';
import {
  activeMemberUsers,
  filterActiveMembers,
  formatTimezoneForDisplay,
  memberTimezoneId,
  NO_MANAGER_FILTER,
  NO_TIMEZONE_FILTER,
  paginateMembers,
} from './active.utils';
import {generateRandomDigits} from '@utils/generate-random-numbers';
import {setFirebaseData} from '@utils/firebase-handler';
import {MemberEditSidecar} from '../../member-edit-sidecar';
import {ManagerRoleChange, ManagerRoleDialog} from '../../manager-role-dialog';
import {
  getManagerIds,
  isLastActiveWorkspaceAdmin,
  isWorkspaceManager,
  reviewerId,
} from '../../member-role';
import {
  ActiveMemberSearchField,
  ActiveMemberSearchIcon,
  ActiveMemberToolbar,
  ConfirmCopy,
  DangerButton,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  GroupMore,
  GroupPill,
  ManagerCell,
  MemberAction,
  MemberActionButton,
  MemberCard,
  MemberCell,
  MemberEmpty,
  MemberFilterOptionCopy,
  MemberFilterOptionIdentity,
  MemberFilterOptionMeta,
  MemberFilterOptionName,
  MemberGroupsCell,
  MemberIdentity,
  MemberIdentityText,
  MemberMenu,
  MemberMenuButton,
  MemberMenuHint,
  MemberMeta,
  MemberName,
  MemberNameLine,
  MemberPageButton,
  MemberPagination,
  MemberRoleBadge,
  MemberSearchInput,
  MemberTableHeader,
  MemberTableRow,
  SecondaryButton,
} from '../../member-redesign.styles';

const userName = (user: any) =>
  user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || 'Unnamed member';

export const Active = observer(() => {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(Number(searchParams.get('page')) || 1, 1);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [menuId, setMenuId] = useState('');
  const [editingMember, setEditingMember] = useState<any>(null);
  const [roleChange, setRoleChange] = useState<ManagerRoleChange | null>(null);
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [confirmation, setConfirmation] = useState<{
    kind: 'suspend' | 'remove';
    member: any;
  } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const {
    groupStore: {groups},
    groupTypeStore: {groupType},
    usersStore: {users: storeUsers, getUsers},
  } = useStoreContext();
  const {initGroupMembers, initUsers} = useApiCalls();

  const fetchActiveUsers = useCallback(async () => {
    const controller = new UserAPIController(new UserAPIRequestImpl());
    return controller.fetchPaginatedConfirmedUsers(page);
  }, [page]);

  const activeUsersQuery = useQuery(['activeUsers', page], fetchActiveUsers);
  const allActiveUsersQuery = useQuery(['activeUsers', 'all'], async () => {
    const controller = new UserAPIController(new UserAPIRequestImpl());
    return controller.fetchAllUsers();
  });
  const confirmedUsersQuery = useQuery(
    ['confirmedUsers', 'last-admin-safety'],
    async () => {
      const controller = new UserAPIController(new UserAPIRequestImpl());
      return controller.fetchConfirmedUsers();
    },
  );
  const groupMembersQuery = useQuery(['initGroupMembers'], initGroupMembers);
  const response = activeUsersQuery.data || {};
  const pageUsers = useMemo(
    () => (Array.isArray(response?.results) ? response.results : []),
    [response?.results],
  );
  const directoryUsers = useMemo(() => {
    const allUsers = activeMemberUsers(allActiveUsersQuery.data);
    if (allUsers.length) return allUsers;
    const storedUsers = activeMemberUsers(storeUsers);
    return storedUsers.length ? storedUsers : pageUsers;
  }, [allActiveUsersQuery.data, pageUsers, storeUsers]);

  const enrichedUsers = useMemo(() => {
    const membership = groupMembersQuery.data;
    return directoryUsers.map((user: any) => {
      const userMembership = membership?.get?.(user.id) || [];
      const groupIds = userMembership.map((item: any) => item.group);
      return {
        ...user,
        group: groups.filter((group: any) => groupIds.includes(group.id)),
      };
    });
  }, [directoryUsers, groupMembersQuery.data, groups]);

  const roleSource = useMemo(() => {
    const merged = new Map<string, any>();
    [...activeMemberUsers(storeUsers), ...enrichedUsers].forEach((user: any) =>
      merged.set(user.id, user),
    );
    return Array.from(merged.values());
  }, [enrichedUsers, storeUsers]);
  const managerIds = useMemo(() => getManagerIds(roleSource), [roleSource]);
  const selectedManagerIds = useMemo(
    () => Array.from(new Set(searchParams.getAll('manager'))),
    [searchParams],
  );
  const selectedTimezoneIds = useMemo(
    () => Array.from(new Set(searchParams.getAll('timezone'))),
    [searchParams],
  );

  const managerOptions = useMemo(() => {
    const options: BeamFilterOption[] = roleSource
      .filter((user: any) => managerIds.has(user.id))
      .sort((first: any, second: any) =>
        userName(first).localeCompare(userName(second)),
      )
      .map((manager: any) => ({
        value: manager.id,
        label: userName(manager),
        searchText: `${userName(manager)} ${manager.jobTitle || ''} ${
          manager.email || ''
        }`,
        content: (
          <MemberFilterOptionIdentity>
            <Avatar
              name={userName(manager)}
              userId={manager.id}
              src={manager.avatar?.url || ''}
              size="sm"
              tooltip={false}
            />
            <MemberFilterOptionCopy>
              <MemberFilterOptionName>
                {userName(manager)}
              </MemberFilterOptionName>
              {(manager.jobTitle || manager.email) && (
                <MemberFilterOptionMeta>
                  {manager.jobTitle || manager.email}
                </MemberFilterOptionMeta>
              )}
            </MemberFilterOptionCopy>
          </MemberFilterOptionIdentity>
        ),
      }));

    if (enrichedUsers.some((user: any) => !reviewerId(user.reviewer))) {
      options.unshift({
        value: NO_MANAGER_FILTER,
        label: 'No manager',
        searchText: 'No manager',
        content: <MemberFilterOptionName>No manager</MemberFilterOptionName>,
      });
    }
    return options;
  }, [enrichedUsers, managerIds, roleSource]);

  const timezoneOptions = useMemo(() => {
    const timezones = new Map<string, string>();
    enrichedUsers.forEach((user: any) => {
      const timezoneId = memberTimezoneId(user);
      if (timezoneId && !timezones.has(timezoneId)) {
        timezones.set(timezoneId, formatTimezoneForDisplay(user.timezone));
      }
    });
    const options = Array.from(timezones.entries())
      .map(([value, label]) => ({
        value,
        label,
        searchText: `${label} ${value}`,
      }))
      .sort((first, second) => first.label.localeCompare(second.label));

    if (enrichedUsers.some((user: any) => !memberTimezoneId(user))) {
      options.unshift({
        value: NO_TIMEZONE_FILTER,
        label: 'Not set',
        searchText: 'Not set',
      });
    }
    return options;
  }, [enrichedUsers]);

  const filterSections = useMemo<BeamFilterSection[]>(
    () => [
      {
        key: 'manager',
        title: 'Manager',
        options: managerOptions,
        selectedValues: selectedManagerIds,
        searchPlaceholder: 'Search managers',
        emptyMessage: 'No managers found.',
      },
      {
        key: 'timezone',
        title: 'Timezone',
        options: timezoneOptions,
        selectedValues: selectedTimezoneIds,
        searchPlaceholder: 'Search timezones',
        emptyMessage: 'No timezones found.',
      },
    ],
    [managerOptions, selectedManagerIds, selectedTimezoneIds, timezoneOptions],
  );

  const confirmedUsers = useMemo(() => {
    const confirmed = confirmedUsersQuery.data;
    if (Array.isArray(confirmed)) return confirmed;
    return Array.isArray(confirmed?.results) ? confirmed.results : [];
  }, [confirmedUsersQuery.data]);
  const hasConfirmedAdminData =
    Array.isArray(confirmedUsersQuery.data) ||
    Array.isArray(confirmedUsersQuery.data?.results);
  const isAdminAccessProtected = useCallback(
    (
      member: any,
      users = confirmedUsers,
      dataAvailable = hasConfirmedAdminData,
    ) =>
      member?.role === 'admin' &&
      (!dataAvailable || isLastActiveWorkspaceAdmin(member, users)),
    [confirmedUsers, hasConfirmedAdminData],
  );

  const filteredUsers = useMemo(
    () =>
      filterActiveMembers({
        users: enrichedUsers,
        search,
        managerIds: selectedManagerIds,
        timezoneIds: selectedTimezoneIds,
      }),
    [enrichedUsers, search, selectedManagerIds, selectedTimezoneIds],
  );

  const pageSize = Math.max(Number(response?.limit || 10), 1);
  const totalPages = Math.max(Math.ceil(filteredUsers.length / pageSize), 1);
  const visibleUsers = useMemo(
    () => paginateMembers(filteredUsers, page, pageSize),
    [filteredUsers, page, pageSize],
  );
  const activeFilterCount =
    selectedManagerIds.length + selectedTimezoneIds.length;

  const appliedFilters = useMemo<BeamAppliedFilter[]>(() => {
    const managerLabels = new Map(
      managerOptions.map((option) => [option.value, option.label]),
    );
    const timezoneLabels = new Map(
      timezoneOptions.map((option) => [option.value, option.label]),
    );
    return [
      ...selectedManagerIds.map((value) => ({
        id: `manager:${value}`,
        label: `Manager: ${managerLabels.get(value) || value}`,
      })),
      ...selectedTimezoneIds.map((value) => ({
        id: `timezone:${value}`,
        label: `Timezone: ${timezoneLabels.get(value) || value}`,
      })),
    ];
  }, [
    managerOptions,
    selectedManagerIds,
    selectedTimezoneIds,
    timezoneOptions,
  ]);

  const setPage = useCallback(
    (nextPage: number) => {
      const params = new URLSearchParams(searchParams);
      params.set('page', String(nextPage));
      setSearchParams(params, {replace: true});
    },
    [searchParams, setSearchParams],
  );

  const updateFilter = useCallback(
    (key: string, value: string, removeOnly = false) => {
      const params = new URLSearchParams(searchParams);
      const currentValues = params.getAll(key);
      const selected = currentValues.includes(value);
      const nextValues = currentValues.filter((current) => current !== value);
      if (!selected && !removeOnly) nextValues.push(value);

      params.delete(key);
      nextValues.forEach((nextValue) => params.append(key, nextValue));
      params.set('page', '1');
      setSearchParams(params, {replace: true});
    },
    [searchParams, setSearchParams],
  );

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('manager');
    params.delete('timezone');
    params.set('page', '1');
    setSearchParams(params, {replace: true});
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, setPage, totalPages]);

  useEffect(() => {
    const closeMenu = () => setMenuId('');
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([
      activeUsersQuery.refetch(),
      allActiveUsersQuery.refetch(),
      groupMembersQuery.refetch(),
      initUsers(),
      getUsers(),
    ]);
    queryClient.invalidateQueries(['activeUsers']);
    queryClient.invalidateQueries(['workspace-member-counts']);
    setFirebaseData('updates/user', generateRandomDigits(16));
  }, [
    activeUsersQuery,
    allActiveUsersQuery,
    getUsers,
    groupMembersQuery,
    initUsers,
    queryClient,
  ]);

  const applyRoleChange = async (userIds: string[]) => {
    if (!roleChange || roleChangeLoading || !userIds.length) return;
    setRoleChangeLoading(true);

    if (roleChange.kind === 'promote') {
      const controller = new UserAPIController(new UserAPIRequestImpl());
      const updated = await controller.updateUsers(
        {userIds, reviewer: roleChange.member.id},
        true,
      );

      if (updated) {
        activateNotification({
          title: 'Manager assigned',
          content: `${userName(roleChange.member)} now manages ${
            userIds.length
          } ${userIds.length === 1 ? 'person' : 'people'}.`,
          kind: 'success',
        });
        setRoleChange(null);
        await refresh();
      }
      setRoleChangeLoading(false);
      return;
    }

    const request = new UserAPIRequestImpl();
    const results = await Promise.allSettled(
      userIds.map((userId) =>
        request.updateUser({removeReviewer: true}, userId),
      ),
    );
    const failed = results.filter((result) => result.status === 'rejected');

    await refresh();
    if (failed.length) {
      activateNotification({
        title: 'Some reports could not be cleared',
        content: `${userIds.length - failed.length} of ${
          userIds.length
        } reporting relationships were removed. Please retry the remaining members.`,
        kind: 'error',
      });
    } else {
      activateNotification({
        title: 'Member updated',
        content: `${userName(
          roleChange.member,
        )} was demoted to member and no longer has direct reports.`,
        kind: 'success',
      });
      setRoleChange(null);
    }
    setRoleChangeLoading(false);
  };

  const performAction = async () => {
    if (!confirmation) return;
    setActionLoading(true);
    const {kind, member} = confirmation;

    if (member?.role === 'admin') {
      const latestConfirmed = await confirmedUsersQuery.refetch();
      const latestData = latestConfirmed.data;
      const latestUsers = Array.isArray(latestData)
        ? latestData
        : Array.isArray(latestData?.results)
        ? latestData.results
        : [];
      const latestDataAvailable =
        Array.isArray(latestData) || Array.isArray(latestData?.results);

      if (isAdminAccessProtected(member, latestUsers, latestDataAvailable)) {
        activateNotification({
          title: 'Another admin is required',
          content:
            'Assign another active workspace admin before suspending or removing this account.',
          kind: 'error',
        });
        setConfirmation(null);
        setActionLoading(false);
        return;
      }
    }

    let success = false;

    if (kind === 'suspend') {
      const response = await fetch(`${BaseURL}/users/${member.id}/deactivate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authStore.auth?.tokens.access.token}`,
        },
      });
      success = response.ok;
    } else {
      const controller = new UserAPIController(new UserAPIRequestImpl());
      success = Boolean(await controller.deleteUser(member.id));
    }

    if (success) {
      activateNotification({
        title: 'Success',
        content:
          kind === 'suspend'
            ? `${userName(member)} was suspended`
            : `${userName(member)} was removed from the workspace`,
        kind: 'success',
      });
      setEditingMember(null);
      setConfirmation(null);
      await refresh();
      queryClient.invalidateQueries(['deactivatedUsers']);
    }
    setActionLoading(false);
  };

  return (
    <>
      <MemberCard>
        <ActiveMemberToolbar>
          <ActiveMemberSearchField>
            <MemberSearchInput
              aria-label="Search members"
              placeholder="Search members"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                if (page !== 1) setPage(1);
              }}
            />
            <ActiveMemberSearchIcon aria-hidden="true">
              <MagnifyingGlassIcon />
            </ActiveMemberSearchIcon>
          </ActiveMemberSearchField>
          <BeamFilterPopover
            title="Filter members"
            sections={filterSections}
            activeCount={activeFilterCount}
            disabled={allActiveUsersQuery.isLoading}
            onToggle={updateFilter}
            onClear={clearFilters}
          />
        </ActiveMemberToolbar>

        <BeamAppliedFilters
          filters={appliedFilters}
          resultLabel={`${filteredUsers.length} ${
            filteredUsers.length === 1 ? 'member' : 'members'
          }`}
          onRemove={(id) => {
            const separator = id.indexOf(':');
            updateFilter(id.slice(0, separator), id.slice(separator + 1), true);
          }}
          onClear={clearFilters}
        />

        <MemberTableHeader>
          <div>Name</div>
          <div>Timezone</div>
          <div>Group(s)</div>
          <div>Manager</div>
          <div style={{textAlign: 'center'}}>Action</div>
        </MemberTableHeader>

        {activeUsersQuery.isLoading ||
        allActiveUsersQuery.isLoading ||
        groupMembersQuery.isLoading ? (
          <div style={{padding: 24}}>
            <ItemLoaderLine />
          </div>
        ) : visibleUsers.length === 0 ? (
          <MemberEmpty>
            {activeFilterCount
              ? 'No members match these filters.'
              : search
              ? 'No members match your search.'
              : 'No active members.'}
          </MemberEmpty>
        ) : (
          visibleUsers.map((user: any) => {
            const isManager = isWorkspaceManager(user, managerIds);
            const protectWorkspaceAccess = isAdminAccessProtected(user);
            const manager =
              typeof user.reviewer === 'object'
                ? user.reviewer
                : roleSource.find(
                    (candidate: any) =>
                      candidate.id === reviewerId(user.reviewer),
                  );
            return (
              <MemberTableRow key={user.id}>
                <MemberIdentity>
                  <Avatar
                    name={userName(user)}
                    userId={user.id}
                    src={user.avatar?.url || ''}
                    size="md"
                    tooltip={true}
                    timezone={user.timezone}
                    customUsers={roleSource}
                  />
                  <MemberIdentityText>
                    <MemberNameLine>
                      <MemberName title={userName(user)}>
                        {userName(user)}
                      </MemberName>
                      {isManager && <MemberRoleBadge>Manager</MemberRoleBadge>}
                    </MemberNameLine>
                    <MemberMeta title={user.jobTitle || user.email}>
                      {user.jobTitle || user.email}
                    </MemberMeta>
                  </MemberIdentityText>
                </MemberIdentity>
                <MemberCell title={formatTimezoneForDisplay(user.timezone)}>
                  {formatTimezoneForDisplay(user.timezone)}
                </MemberCell>
                <MemberGroupsCell>
                  {user.group?.length ? (
                    <>
                      <GroupPill title={user.group[0].name}>
                        {user.group[0].name}
                      </GroupPill>
                      {user.group.length > 1 && (
                        <Tooltip
                          BodyTextNodeType={GroupMore}
                          text={`+${user.group.length - 1}`}
                          noCapitalize
                          bodyStyle={{cursor: 'help'}}
                          tooltipBody={user.group
                            .slice(1)
                            .map((group: any) => group.name)
                            .join(', ')}
                        />
                      )}
                    </>
                  ) : (
                    '—'
                  )}
                </MemberGroupsCell>
                <ManagerCell>
                  {manager ? (
                    <>
                      <Avatar
                        name={userName(manager)}
                        userId={manager.id || reviewerId(user.reviewer)}
                        src={manager.avatar?.url || ''}
                        size="sm"
                        tooltip={false}
                      />
                      <span title={userName(manager)}>{userName(manager)}</span>
                    </>
                  ) : (
                    '—'
                  )}
                </ManagerCell>
                <MemberAction onClick={(event) => event.stopPropagation()}>
                  <MemberActionButton
                    type="button"
                    aria-label={`Actions for ${userName(user)}`}
                    aria-expanded={menuId === user.id}
                    onClick={() =>
                      setMenuId(menuId === user.id ? '' : user.id)
                    }>
                    <HorizontalDots />
                  </MemberActionButton>
                  {menuId === user.id && (
                    <MemberMenu>
                      <MemberMenuButton
                        type="button"
                        onClick={() => {
                          setEditingMember(user);
                          setMenuId('');
                        }}>
                        Edit profile
                      </MemberMenuButton>
                      {user.role !== 'admin' && (
                        <MemberMenuButton
                          type="button"
                          onClick={() => {
                            setRoleChange({
                              kind: isManager ? 'demote' : 'promote',
                              member: user,
                            });
                            setMenuId('');
                          }}>
                          {isManager
                            ? 'Demote to member'
                            : 'Promote to manager'}
                        </MemberMenuButton>
                      )}
                      <MemberMenuButton
                        type="button"
                        $warning
                        disabled={protectWorkspaceAccess}
                        title={
                          protectWorkspaceAccess
                            ? 'Assign another workspace admin first.'
                            : undefined
                        }
                        onClick={() => {
                          setConfirmation({kind: 'suspend', member: user});
                          setMenuId('');
                        }}>
                        Suspend member
                      </MemberMenuButton>
                      <MemberMenuButton
                        type="button"
                        $danger
                        disabled={protectWorkspaceAccess}
                        title={
                          protectWorkspaceAccess
                            ? 'Assign another workspace admin first.'
                            : undefined
                        }
                        onClick={() => {
                          setConfirmation({kind: 'remove', member: user});
                          setMenuId('');
                        }}>
                        Remove from workspace
                      </MemberMenuButton>
                      {protectWorkspaceAccess && (
                        <MemberMenuHint>
                          At least one active workspace admin is required.
                        </MemberMenuHint>
                      )}
                    </MemberMenu>
                  )}
                </MemberAction>
              </MemberTableRow>
            );
          })
        )}

        {totalPages > 1 && (
          <MemberPagination>
            <MemberPageButton
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}>
              ‹
            </MemberPageButton>
            {Array.from({length: totalPages}, (_, index) => index + 1)
              .filter(
                (number) =>
                  number === 1 ||
                  number === totalPages ||
                  Math.abs(number - page) <= 1,
              )
              .map((number) => (
                <MemberPageButton
                  type="button"
                  key={number}
                  $active={number === page}
                  onClick={() => setPage(number)}>
                  {number}
                </MemberPageButton>
              ))}
            <MemberPageButton
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}>
              ›
            </MemberPageButton>
          </MemberPagination>
        )}
      </MemberCard>

      {editingMember && (
        <MemberEditSidecar
          member={editingMember}
          users={roleSource}
          groups={groups}
          groupTypes={groupType}
          isManager={isWorkspaceManager(editingMember, managerIds)}
          protectWorkspaceAccess={isAdminAccessProtected(editingMember)}
          onClose={() => setEditingMember(null)}
          onSaved={refresh}
          onSuspend={(member) => {
            setEditingMember(null);
            setConfirmation({kind: 'suspend', member});
          }}
        />
      )}

      {roleChange && (
        <ManagerRoleDialog
          kind={roleChange.kind}
          member={roleChange.member}
          users={roleSource}
          managerIds={managerIds}
          loading={roleChangeLoading}
          onClose={() => setRoleChange(null)}
          onApply={applyRoleChange}
        />
      )}

      {confirmation && (
        <DialogOverlay>
          <Dialog role="dialog" aria-modal="true" aria-label="Confirm action">
            <DialogHeader>
              <DialogTitle>
                {confirmation.kind === 'suspend'
                  ? 'Suspend member'
                  : 'Remove member'}
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ConfirmCopy>
                {confirmation.kind === 'suspend'
                  ? `${userName(
                      confirmation.member,
                    )} will lose access until an admin restores the account.`
                  : `Remove ${userName(
                      confirmation.member,
                    )} and their workspace access? This action cannot be undone.`}
              </ConfirmCopy>
            </DialogBody>
            <DialogFooter>
              <SecondaryButton
                type="button"
                disabled={actionLoading}
                onClick={() => setConfirmation(null)}>
                Cancel
              </SecondaryButton>
              <DangerButton
                type="button"
                disabled={actionLoading}
                onClick={performAction}>
                {actionLoading
                  ? 'Working…'
                  : confirmation.kind === 'suspend'
                  ? 'Suspend member'
                  : 'Remove member'}
              </DangerButton>
            </DialogFooter>
          </Dialog>
        </DialogOverlay>
      )}
    </>
  );
});
